import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, ShieldAlert, HelpCircle, Clock, ExternalLink } from "lucide-react";
import { formatDate } from "@/lib/checklist";

const OUTCOME_META = {
  clear: {
    label: "Clear — no restrictions",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Icon: CheckCircle2,
  },
  restrictions_found: {
    label: "Restrictions found",
    cls: "bg-rose-50 text-rose-700 border-rose-200",
    Icon: ShieldAlert,
  },
  not_found: {
    label: "Not on Update Service",
    cls: "bg-amber-50 text-amber-800 border-amber-200",
    Icon: HelpCircle,
  },
};

/**
 * DBSPanel — DBS Update Service verification for any worker.
 * Renders the verification details (cert #, surname, DOB) captured at upload,
 * plus the history of manager/team checks and a "Record DBS check" action.
 */
export default function DBSPanel({ workerId, document, canReview, onChanged }) {
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);

  const meta = document?.metadata || {};

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/workers/${workerId}/dbs-checks`);
      setHistory(data);
    } catch (_e) {
      /* ignore */
    }
  }, [workerId]);

  useEffect(() => {
    if (workerId) loadHistory();
  }, [workerId, loadHistory]);

  const latest = history[0];
  const latestMeta = latest ? OUTCOME_META[latest.outcome] : null;
  const LatestIcon = latestMeta?.Icon;

  return (
    <div
      data-testid="dbs-panel"
      className="mt-4 rounded-lg border border-teal-100 bg-teal-50/40 p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-teal-600/10 border border-teal-100 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-teal-700" />
          </div>
          <div>
            <div className="label-caps text-slate-500">DBS Update Service verification</div>
            <div className="text-sm text-slate-600 mt-0.5">
              Use these details to look up the worker on the
              <a
                href="https://secure.crbonline.gov.uk/enquiry/enquirySearch.do"
                target="_blank"
                rel="noreferrer"
                className="text-teal-700 hover:underline mx-1 inline-flex items-center gap-0.5"
              >
                DBS Update Service <ExternalLink className="h-3 w-3" />
              </a>
              then record the outcome below.
            </div>
          </div>
        </div>
        {latest && latestMeta && (
          <span
            data-testid="dbs-latest-outcome"
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${latestMeta.cls}`}
          >
            <LatestIcon className="h-3.5 w-3.5" />
            {latestMeta.label}
          </span>
        )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <DetailCell label="Certificate number" value={meta.certificate_number} testid="dbs-cert-number" mono />
        <DetailCell label="Surname (from profile)" value={meta.surname} testid="dbs-surname" />
        <DetailCell label="Date of birth" value={meta.dob ? formatDate(meta.dob) : null} testid="dbs-dob" />
      </div>

      <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
        <div className="label-caps text-slate-500">Last check</div>
        {latest ? (
          <div className="mt-1 text-sm text-slate-800">
            {formatDate(latest.checked_at)} · by {latest.checked_by_name}
            {latest.notes && (
              <div className="text-xs text-slate-500 mt-1">{latest.notes}</div>
            )}
          </div>
        ) : (
          <div className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> No check recorded yet.
          </div>
        )}
      </div>

      {canReview && (
        <div className="mt-4">
          <Button
            onClick={() => setOpen(true)}
            data-testid="dbs-record-check-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            Record DBS check
          </Button>
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-4" data-testid="dbs-history">
          <summary className="text-sm font-semibold text-slate-700 cursor-pointer">
            View history ({history.length})
          </summary>
          <div className="mt-2 space-y-2">
            {history.map((h) => {
              const om = OUTCOME_META[h.outcome];
              const OIcon = om?.Icon;
              return (
                <div
                  key={h.id}
                  data-testid={`dbs-history-row-${h.id}`}
                  className="rounded-md border border-slate-200 bg-white p-3 text-sm"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-medium text-slate-900">{formatDate(h.checked_at)}</span>
                      <span className="text-slate-500"> · {h.checked_by_name}</span>
                    </div>
                    {om && (
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${om.cls}`}
                      >
                        <OIcon className="h-3 w-3" />
                        {om.label}
                      </span>
                    )}
                  </div>
                  {h.notes && <div className="text-slate-600 mt-1">{h.notes}</div>}
                </div>
              );
            })}
          </div>
        </details>
      )}

      <RecordCheckDialog
        open={open}
        onOpenChange={setOpen}
        workerId={workerId}
        onSubmitted={() => {
          loadHistory();
          onChanged && onChanged();
        }}
      />
    </div>
  );
}

function DetailCell({ label, value, testid, mono }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="label-caps text-slate-500">{label}</div>
      <div
        data-testid={testid}
        className={`mt-1 text-sm text-slate-900 font-semibold ${mono ? "font-mono tracking-tight" : ""}`}
      >
        {value || <span className="text-slate-400 font-normal">—</span>}
      </div>
    </div>
  );
}

function RecordCheckDialog({ open, onOpenChange, workerId, onSubmitted }) {
  const [outcome, setOutcome] = useState("clear");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setOutcome("clear");
      setNotes("");
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workers/${workerId}/dbs-check`, { outcome, notes });
      toast.success("DBS check recorded");
      onOpenChange(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error("Could not record DBS check", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="dbs-check-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Record DBS Update Service check</DialogTitle>
          <DialogDescription>
            Confirm the outcome you saw on the DBS Update Service. Logged as of today with your name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="label-caps">Outcome</Label>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger data-testid="dbs-outcome-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="clear">Clear — no restrictions</SelectItem>
              <SelectItem value="restrictions_found">Restrictions found</SelectItem>
              <SelectItem value="not_found">Not found on Update Service</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="label-caps">Notes (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="dbs-check-notes"
            placeholder="e.g. matched name and DOB, no changes since issue"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="dbs-check-submit"
            className="bg-teal-700 hover:bg-teal-800"
          >
            {busy ? "Saving…" : "Save check"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
